import React, { useMemo, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import PropTypes from "prop-types"
import DataTable from "ui/DataTable"
import { getHeaders } from "./utils/headers"
import { useStaffTable } from "./hooks/useStaffTable"
import { transformStaffToRow } from "./components/StaffTableRow"
import StaffModals from "./components/StaffModals"
import MobileStaffTable from "../mobileStaffTable"
import { useDevice } from "hooks/useDevice"
import styles from "./index.module.css"

import {
  selectCheckDeactivationLoading,
  selectCheckDeletionLoading,
  selectDeactivateStaffLoading,
  selectReactivateStaffLoading,
  selectDeleteStaffLoading
} from "store/staff"

const StaffTable = ({
  staff,
  mobileStaff,
  sortField,
  sortOrder,
  handleTableChange,
  loading,
  onEdit,
  onRefresh,
  currentPage,
  totalPages,
  onLoadMore,
  totalResults
}) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const userRole = useSelector((state) => state.user.role)
  const currentGuideProfileId = useSelector((state) => state.user.active_profile?.id)
  const deactivationDetailsLoading = useSelector(selectCheckDeactivationLoading)
  const deletionDetailsLoading = useSelector(selectCheckDeletionLoading)
  const deactivateLoading = useSelector(selectDeactivateStaffLoading)
  const activateLoading = useSelector(selectReactivateStaffLoading)
  const deleteLoading = useSelector(selectDeleteStaffLoading)

  const {
    deactivateModalVisible,
    activateModalVisible,
    deleteModalVisible,
    selectedStaff,
    deactivateDeleteMode,
    deactivateDeleteDetails,
    getMenuItems,
    handleView,
    handleDeactivateConfirm,
    handleActivateConfirm,
    handleDeleteConfirm,
    handleModalCancel
  } = useStaffTable(t, onEdit, userRole, onRefresh, currentGuideProfileId)

  const columns = useMemo(() => getHeaders({ field: sortField, order: sortOrder }, t), [sortField, sortOrder, t])

  const dataSource = useMemo(() => {
    return staff?.map((staffMember) => transformStaffToRow(staffMember, getMenuItems, t))
  }, [staff, getMenuItems, t])

  const handleRowClick = useCallback(
    (id) => {
      handleView(id)
    },
    [handleView]
  )

  return (
    <div>
      {isMobile ? (
        <MobileStaffTable
          staff={mobileStaff}
          loading={loading}
          currentPage={currentPage}
          totalPages={totalPages}
          onLoadMore={onLoadMore}
          totalResults={totalResults}
          getMenuItems={getMenuItems}
        />
      ) : (
        <DataTable
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          rowClickKey="key"
          onChange={handleTableChange}
          onRowClick={handleRowClick}
          rowKey={(record) => record.key}
          loading={loading}
          rowClassName={() => styles.clickableRow}
        />
      )}
      <StaffModals
        deactivateModalVisible={deactivateModalVisible}
        activateModalVisible={activateModalVisible}
        deleteModalVisible={deleteModalVisible}
        selectedStaff={selectedStaff}
        deactivateDeleteMode={deactivateDeleteMode}
        deactivateDeleteDetails={deactivateDeleteDetails}
        onDeactivateConfirm={handleDeactivateConfirm}
        onActivateConfirm={handleActivateConfirm}
        onDeleteConfirm={handleDeleteConfirm}
        onCancel={handleModalCancel}
        deactivateLoading={deactivateLoading}
        deleteLoading={deleteLoading}
        activateLoading={activateLoading}
        deactivationDetailsLoading={deactivationDetailsLoading}
        deletionDetailsLoading={deletionDetailsLoading}
        t={t}
      />
    </div>
  )
}

StaffTable.propTypes = {
  staff: PropTypes.array,
  mobileStaff: PropTypes.array,
  sortField: PropTypes.string,
  sortOrder: PropTypes.string,
  handleTableChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onEdit: PropTypes.func,
  onRefresh: PropTypes.func,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  onLoadMore: PropTypes.func,
  totalResults: PropTypes.number
}

StaffTable.defaultProps = {
  staff: []
}

export default StaffTable
